import { createMachine, assign, fromPromise } from 'xstate'
import { eventBus } from '@/utils/eventBus'
import type { ModalContext, ModalEvent } from './types'

const operationActor = fromPromise<any, { operation: string; data: any }>(async ({ input }) => {
  const { operation, data } = input

  // 这里可以根据不同的操作类型执行不同的逻辑
  switch (operation) {
    case 'updateOrder':
      // 模拟订单更新操作
      await new Promise((resolve) => setTimeout(resolve, 2000))
      if (Math.random() > 0.7) {
        // 30% 失败率用于测试
        throw new Error('订单更新失败')
      }
      return { success: true, orderId: data.orderId }

    case 'closeOrder':
      // 模拟订单关闭操作
      await new Promise((resolve) => setTimeout(resolve, 1500))
      if (Math.random() > 0.8) {
        // 20% 失败率
        throw new Error('订单关闭失败')
      }
      return { success: true, orderId: data.orderId }

    default:
      throw new Error(`Unknown operation: ${operation}`)
  }
})

export const modalStateMachine = createMachine({
  types: {} as {
    context: ModalContext
    events: ModalEvent
  },
  id: 'modal',
  initial: 'closed',
  context: {
    modalId: '',
    type: '',
    payload: {},
    retryCount: 0,
    maxRetries: 3,
    startTime: 0,
  },
  states: {
    closed: {
      on: {
        OPEN: {
          target: 'opening',
          actions: assign({
            modalId: ({ event }) => event.payload.modalId,
            type: ({ event }) => event.payload.type,
            payload: ({ event }) => event.payload.data,
            startTime: () => Date.now(),
            retryCount: 0,
          }),
        },
      },
    },

    opening: {
      entry: [
        // 发送弹窗打开事件
        ({ context }) => {
          eventBus.emit('modal.opened', {
            modalId: context.modalId,
            type: context.type,
            payload: context.payload,
            timestamp: Date.now(),
          })
        },
      ],

      after: {
        100: 'open', // 短暂延迟后进入打开状态
      },
    },

    open: {
      entry: [
        // 增加用户交互计数
        assign({
          // analytics: ({ context }) => ({
          //   ...context.analytics,
          //   userInteractions: context.analytics.userInteractions + 1,
          // }),
        }),
      ],

      on: {
        CONFIRM: {
          target: 'processing',
          actions: assign({
            data: ({ event }) => event.data,
          }),
        },

        CANCEL: {
          target: 'closing',
          actions: [
            ({ context, event }) => {
              eventBus.emit('modal.cancelled', {
                modalId: context.modalId,
                reason: event.reason,
                timestamp: Date.now(),
              })
            },
          ],
        },

        FORCE_CLOSE: 'closing',
      },

      // 弹窗超时自动关闭
      after: {
        300000: {
          // 5分钟超时
          target: 'closing',
          actions: [
            ({ context }) => {
              eventBus.emit('modal.cancelled', {
                modalId: context.modalId,
                reason: 'timeout',
                timestamp: Date.now(),
              })
            },
          ],
        },
      },
    },

    confirming: {
      entry: [
        ({ context }) => {
          eventBus.emit('modal.confirmed', {
            modalId: context.modalId,
            data: context.data,
            timestamp: Date.now(),
          })
        },
      ],

      on: {
        START_OPERATION: {
          target: 'processing',
          actions: assign({
            // operationId: ({ event }) => event.operation,
          }),
        },

        // 如果没有异步操作，直接关闭
        FORCE_CLOSE: 'closing',
      },
    },

    processing: {
      entry: [
        assign({
          // analytics: ({ context }) => ({
          //   ...context.analytics,
          //   userInteractions: context.analytics.userInteractions + 1,
          // }),
        }),
        ({ context }) => {
          eventBus.emit('modal.pending', {
            modalId: context.modalId,
            pending: true,
            timestamp: Date.now(),
          })
        },
      ],

      on: {
        OPERATION_SUCCESS: {
          target: 'success',
          actions: assign({
            data: ({ event }) => event.result,
          }),
        },

        OPERATION_FAILED: {
          target: 'error',
          actions: assign({
            error: ({ event }) => ({
              message: event.error.message || '操作失败',
              code: event.error.code || 'UNKNOWN_ERROR',
              retryable: event.error.retryable !== false,
            }),
          }),
        },
      },
    },

    error: {
      entry: [
        ({ context }) => {
          eventBus.emit('modal.pending', {
            modalId: context.modalId,
            pending: false,
            timestamp: Date.now(),
          })
        },
      ],

      on: {
        CONFIRM: {
          target: 'processing',
          actions: assign({
            data: ({ event }) => event.data,
            error: undefined,
          }),
        },

        RETRY: [
          {
            target: 'processing',
            guard: ({ context }) => context.retryCount < context.maxRetries,
            actions: assign({
              retryCount: ({ context }) => context.retryCount + 1,
              error: undefined,
            }),
          },
          {
            // 达到最大重试次数，保持在错误状态
            actions: assign({
              error: ({ context }) => ({
                ...context.error!,
                message: `操作失败，已达到最大重试次数 (${context.maxRetries})`,
              }),
            }),
          },
        ],

        CANCEL: 'closing',
        FORCE_CLOSE: 'closing',
      },
    },

    success: {
      entry: [
        ({ context }) => {
          const timeSpent = Date.now() - context.startTime
          eventBus.emit('modal.closed', {
            modalId: context.modalId,
            result: context.data,
            timestamp: Date.now(),
          })

          // 发送分析数据
          // eventBus.emit('analytics.track', {
          //   event: 'modal.completed',
          //   properties: {
          //     modalType: context.type,
          //     timeSpent,
          //     userInteractions: context.analytics.userInteractions,
          //     retryCount: context.retryCount,
          //     success: true,
          //   },
          //   timestamp: Date.now(),
          // })
        },
      ],

      after: {
        0: 'closing', // 立即关闭，不等待
      },
    },

    closing: {
      entry: [
        ({ context }) => {
          if (context.startTime > 0) {
            const timeSpent = Date.now() - context.startTime

            // 发送分析数据
            // eventBus.emit('analytics.track', {
            //   event: 'modal.closed',
            //   properties: {
            //     modalType: context.type,
            //     timeSpent,
            //     userInteractions: context.analytics.userInteractions,
            //     errorCount: context.analytics.errorCount,
            //     completed: !!context.data,
            //   },
            //   timestamp: Date.now(),
            // })
          }
        },
      ],

      after: {
        200: 'closed', // 短暂延迟后完全关闭
      },
    },
  },
})

// 导出状态机配置
export type ModalMachine = typeof modalStateMachine
